import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { removeAllCalls, removeCall } from '@rsos/reduxDataStore';

export type KeywordDetectionMessage = {
  keywordSetName: string;
  keywordSetId: string;
  callerId: string;
};

export interface KeywordNotificationsState {
  keywordDetections: KeywordDetectionMessage[];

  // track notifications that have already been shown to prevent duplicates
  shownNotifications: {
    [callerId: string]: {
      [kewordSetId: string]: true;
    };
  };
}

const initialState: KeywordNotificationsState = {
  keywordDetections: [],
  shownNotifications: {},
};

const keywordNotificationsSlice = createSlice({
  name: 'keywordNotifications',
  initialState,
  reducers: {
    keywordDetectionRecieved: (
      state,
      action: PayloadAction<KeywordDetectionMessage>,
    ) => {
      state.keywordDetections.push(action.payload);
    },
    // Record that we've shown a notification for a specific keyword set in a specific call
    keywordNotificationShown: (
      state,
      action: PayloadAction<KeywordDetectionMessage>,
    ) => {
      const { callerId, keywordSetId } = action.payload;
      state.shownNotifications[callerId] ||= {};
      state.shownNotifications[callerId][keywordSetId] = true;
    },
  },
  extraReducers: builder =>
    builder
      .addCase(
        removeCall,
        (state: KeywordNotificationsState, action: { payload: string }) => {
          delete state.shownNotifications[action.payload];
          state.keywordDetections = state.keywordDetections.filter(
            d => d.callerId !== action.payload,
          );
        },
      )
      .addCase(removeAllCalls, () => {
        return initialState;
      }),
});

interface StateWithKeywordNotifications {
  keywordNotifications: KeywordNotificationsState;
}

export const keywordNotificationsSelector = (
  state: StateWithKeywordNotifications,
) => {
  return state.keywordNotifications;
};

// Returns true if a notification has already shown for this call and keyword
export const useKeywordHasNotified = () => {
  const { shownNotifications } = useSelector(keywordNotificationsSelector);
  return (detection: KeywordDetectionMessage) => {
    const { callerId, keywordSetId } = detection;
    return (shownNotifications[callerId] || {})[keywordSetId];
  };
};

export const { keywordDetectionRecieved, keywordNotificationShown } =
  keywordNotificationsSlice.actions;
export default keywordNotificationsSlice.reducer;
